import React, { useCallback, useEffect, useRef, useState } from "react";

const DEFAULT_RECT = { x: 0.05, y: 0.05, width: 0.9, height: 0.9 };
const MIN_SIZE = 0.08;

const clampRect = (rect) => {
  const width = Math.max(MIN_SIZE, Math.min(1, Number(rect.width) || MIN_SIZE));
  const height = Math.max(MIN_SIZE, Math.min(1, Number(rect.height) || MIN_SIZE));
  const x = Math.max(0, Math.min(1 - width, Number(rect.x) || 0));
  const y = Math.max(0, Math.min(1 - height, Number(rect.y) || 0));
  return { x, y, width, height };
};

/**
 * Freeform web crop modal — drag to move, corner handles to resize.
 * Saves a normalized rect { x, y, width, height } in 0–1 image space.
 */
export default function WebCropModal({
  open,
  imageUrl,
  label,
  initialRect = DEFAULT_RECT,
  onSave,
  onClose,
}) {
  const stageRef = useRef(null);
  const winRef = useRef(null);
  const imgRef = useRef(null);
  const drag = useRef(null);
  const rectRef = useRef(clampRect(initialRect || DEFAULT_RECT));
  const [, forceUpdate] = useState(0);

  useEffect(() => {
    rectRef.current = clampRect(initialRect || DEFAULT_RECT);
    forceUpdate((n) => n + 1);
  }, [initialRect, open, imageUrl]);

  const applyRect = useCallback(() => {
    if (!stageRef.current || !winRef.current) return;
    const stage = stageRef.current;
    const win = winRef.current;
    const W = stage.clientWidth;
    const H = stage.clientHeight;
    if (!W || !H) return;
    const r = rectRef.current;
    win.style.left = `${r.x * W}px`;
    win.style.top = `${r.y * H}px`;
    win.style.width = `${r.width * W}px`;
    win.style.height = `${r.height * H}px`;
  }, []);

  useEffect(() => {
    if (!open) return;
    const img = imgRef.current;
    if (img && img.complete && img.clientHeight > 0) applyRect();
    else if (img) img.onload = applyRect;
  }, [open, applyRect, imageUrl]);

  useEffect(() => {
    if (!open) return;
    const win = winRef.current;
    if (!win) return;

    const readRectFromDom = () => {
      const stage = stageRef.current;
      if (!stage) return rectRef.current;
      const W = stage.clientWidth || 1;
      const H = stage.clientHeight || 1;
      return clampRect({
        x: (parseFloat(win.style.left) || 0) / W,
        y: (parseFloat(win.style.top) || 0) / H,
        width: (win.clientWidth || 0) / W,
        height: (win.clientHeight || 0) / H,
      });
    };

    const onDown = (e) => {
      const handle = e.target?.dataset?.handle;
      const stage = stageRef.current;
      if (!stage) return;
      const start = readRectFromDom();
      drag.current = {
        handle: handle || "move",
        startX: e.clientX,
        startY: e.clientY,
        start,
        stageW: stage.clientWidth,
        stageH: stage.clientHeight,
      };
      win.classList.add("dragging");
      win.setPointerCapture?.(e.pointerId);
      e.preventDefault();
    };

    const onMove = (e) => {
      if (!drag.current) return;
      const { handle, startX, startY, start, stageW, stageH } = drag.current;
      const dx = (e.clientX - startX) / stageW;
      const dy = (e.clientY - startY) / stageH;
      let next = { ...start };

      if (handle === "move") {
        next.x = start.x + dx;
        next.y = start.y + dy;
      } else {
        if (handle.includes("e")) {
          next.width = start.width + dx;
        }
        if (handle.includes("s")) {
          next.height = start.height + dy;
        }
        if (handle.includes("w")) {
          next.x = start.x + dx;
          next.width = start.width - dx;
        }
        if (handle.includes("n")) {
          next.y = start.y + dy;
          next.height = start.height - dy;
        }
      }

      rectRef.current = clampRect(next);
      applyRect();
    };

    const onUp = () => {
      drag.current = null;
      win.classList.remove("dragging");
    };

    win.addEventListener("pointerdown", onDown);
    win.addEventListener("pointermove", onMove);
    win.addEventListener("pointerup", onUp);
    win.addEventListener("pointercancel", onUp);
    return () => {
      win.removeEventListener("pointerdown", onDown);
      win.removeEventListener("pointermove", onMove);
      win.removeEventListener("pointerup", onUp);
      win.removeEventListener("pointercancel", onUp);
    };
  }, [open, applyRect]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  if (!open || !imageUrl) return null;

  const handleSave = () => {
    onSave?.(clampRect(rectRef.current));
  };

  const handleReset = () => {
    rectRef.current = { ...DEFAULT_RECT };
    applyRect();
    forceUpdate((n) => n + 1);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: "rgba(0,0,0,0.45)", backdropFilter: "blur(2px)" }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full"
        style={{ maxWidth: 560, padding: "24px 26px" }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-[15px] font-bold mb-1">
          Adjust web crop{label ? ` — ${label}` : ""}
        </h3>
        <p className="text-[12.5px] text-darkgray mb-4">
          Drag to move or use the corners to resize. Print keeps its full frame.
        </p>

        <div ref={stageRef} className="editor-crop-stage">
          <img
            ref={imgRef}
            src={imageUrl}
            alt="Crop preview"
            className="w-full block"
            draggable={false}
          />
          <div ref={winRef} className="editor-crop-window">
            {["nw", "ne", "sw", "se"].map((handle) => (
              <span
                key={handle}
                data-handle={handle}
                className={`editor-crop-handle editor-crop-handle-${handle}`}
              />
            ))}
          </div>
        </div>

        <div className="flex justify-between mt-4 gap-2">
          <button
            type="button"
            onClick={handleReset}
            className="border border-mediumgray bg-white rounded-lg px-[18px] py-[9px] text-[13px] font-semibold hover:bg-lightgray"
          >
            Reset
          </button>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="border border-mediumgray bg-white rounded-lg px-[18px] py-[9px] text-[13px] font-semibold hover:bg-lightgray"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="bg-black text-white rounded-lg px-[18px] py-[9px] text-[13px] font-semibold hover:opacity-85"
            >
              Save crop
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
